/* 대화방 표시 규칙 — 레퍼런스 v2-live.js messageList/snapshot, v2-chat.js timeOf/fields, chat.js sugText.
   서버 방(Room)을 화면 한 줄(ChatItem) 목록으로 바꾼다. 화면은 이 목록만 그린다. */
import { pickBoardValues } from '@/features/veluvit/lib/custom-screen'
import { fmtTime } from '@/features/veluvit/lib/format'
import { gridItems, stageOf } from '@/features/veluvit/lib/rules'

import type {
  CharacterImage,
  CommandData,
  Room,
  Segment,
  StatusSnapshot,
  Suggestion,
} from '@/features/veluvit/types/api.types'
import type { Catalog } from '@/features/veluvit/types/catalog.types'

export type ChatStatus = StatusSnapshot & {
  time?: number | null
  aff: number
  story: boolean
}

export function sceneImages(
  catalog: Catalog,
  workId: string,
  dialogueImages: string[],
  room?: Pick<Room, 'workId' | 'flags'>
): string[] {
  const flags = new Set(room?.workId === workId ? room.flags : [])
  const occurred = gridItems(catalog, workId)
    .filter((item) => !item.adult && flags.has(`moment:${item.cardId}`))
    .map((item) => item.img)
  const images = Array.from(
    new Set([...dialogueImages, ...occurred].filter(Boolean))
  )
  if (images.length) return images
  return [
    catalog.images[workId]?.s1 || catalog.images[workId]?.thumb || '',
  ].filter(Boolean)
}

export function isPlayerSegment(segment: Segment) {
  return (
    segment.type === 'user' ||
    (segment.type === 'speech' &&
      (segment.speakerType === 'player' ||
        segment.speakerId === 'user' ||
        segment.provenance === 'model_generated_player_dialogue'))
  )
}
type SegmentMeta = {
  segment?: Segment
  messageId?: string
  requestId?: string
  channel?: 'dialogue' | 'ooc' | string
  source?: string
}

export type ChatItem =
  | ({
      t: 'user'
      key: string
      v: string
      turnId?: string | null
      aiPlayer?: boolean
    } & SegmentMeta)
  | ({
      t: 'char'
      key: string
      v: string
      sp?: string
      replyId?: string
    } & SegmentMeta)
  | ({ t: 'sit'; key: string; v: string; replyId?: string } & SegmentMeta)
  | {
      t: 'sys'
      key: string
      v: string
      messageId?: string
      segments?: Segment[]
      channel?: string
    }
  | { t: 'persona'; key: string; name: string }
  | ({
      t: 'image'
      key: string
      src: string
      replyId?: string
      portrait?: CharacterImage
      speakerName?: string
    } & SegmentMeta)
  | { t: 'kindImage'; key: string; kind: string }
  /** values — 그 턴의 작가 상태창 값(statusValues). 작가 상태창이 있는 작품은 이걸로 그린다 */
  | {
      t: 'status'
      key: string
      snap: ChatStatus
      mini: boolean
      values?: Record<string, string>
      source?: string
    }
  | {
      t: 'cmd'
      key: string
      data: CommandData
      snap: ChatStatus
      replyId?: string
    }
  | { t: 'compat'; key: string; snap: ChatStatus }
  | { t: 'error'; key: string; v: string; channel?: 'dialogue' | 'ooc' }

/** 작품 기본 상태 + 서버 상태 → 화면 상태 (v2 snapshot) */
export function snapshot(
  catalog: Catalog,
  workId: string,
  status: StatusSnapshot | undefined,
  affinity?: number
): ChatStatus {
  const base = catalog.chars[workId]?.status || {
    date: '',
    time: null,
    place: '',
  }
  const s = status || {}
  const time = s.minutes ?? s.time ?? (status ? null : (base.time ?? null))
  return {
    ...(!status ? base : {}),
    ...s,
    time,
    expr: s.expression || s.expr || (!status ? base.expr : undefined),
    aff: affinity == null ? s.affinity || 0 : affinity,
    story: (catalog.chars[workId]?.ptype || '') === '스토리챗',
  }
}

/** 상태창 시각 표시 — 모르는 시각은 비워 둔다 (v2-chat timeOf) */
export function timeOf(status: ChatStatus | StatusSnapshot): string {
  if (status.noTime)
    return typeof status.timeLabel === 'string' ? status.timeLabel : ''
  const minutes = status.minutes == null ? status.time : status.minutes
  if (typeof minutes === 'string' && /^\d{1,2}:\d{2}$/.test(minutes))
    return minutes
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return ''
  return fmtTime(minutes)
}

/** 상태창 항목 — 값이 있는 것만 */
export function statusFields(
  status: ChatStatus | StatusSnapshot
): [string, string][] {
  const rows: [string, unknown][] = [
    ['날짜', status.date],
    ['시간', timeOf(status)],
    ['장소', status.place],
    ['날씨', status.weather],
    ['표정', status.expression || status.expr],
    ['복장', status.outfit],
    ['내 복장', status.userOutfit],
    ['속마음', status.inner],
  ]
  return rows.filter(
    (row): row is [string, string] =>
      typeof row[1] === 'string' && !!row[1].trim()
  )
}

/** 서버 방 메시지 → 화면 목록 (v2 messageList) */
export function messageList(
  catalog: Catalog,
  room: Room,
  channel: 'dialogue' | 'ooc' = 'dialogue'
): ChatItem[] {
  const result: ChatItem[] = []
  let lastStatus: ChatStatus | null = null
  let lastSceneImage: string | null = null
  const lastPortraitBySpeaker = new Map<
    string,
    { albumId: string; image: string }
  >()
  const portraitKeyCounts = new Map<string, number>()
  // 묶음(내 말 + 답장)의 상태창은 묶음 끝에 하나만 — 답장이 여러 메시지로 오면 메시지마다 statusSnapshot 이 붙어 사이사이에 끼었다.
  // 새 묶음이 시작될 때 직전 묶음 것을 내려놓는다. 새 묶음 = 이용자가 말한 메시지, 또는 ⏩ 입력 없이 계속으로 AI 가 내 대사를 써서 시작한 답장(2026-10-13 결정: ⏩ 는 아래에 새 대화를 연다).
  // AI 내 대사 없이 캐릭터만 이어진 답장은 앞 묶음에 붙은 것으로 보고 끼우지 않는다.
  // 인트로는 서버가 줄마다 메시지 하나(이용자 줄은 role user)로 만들고 줄마다 statusSnapshot 을 붙인다 — 인트로 전체를 한 묶음으로 본다
  let pending: { item: ChatItem; opening: boolean } | null = null
  const flush = () => {
    if (pending) result.push(pending.item)
    pending = null
  }
  const workId = room.workId
  for (const m of (channel === 'ooc' ? room.oocMessages : room.messages) ||
    []) {
    const sameOpening = pending?.opening && m.source === 'opening'
    const playerLed =
      m.role === 'assistant' &&
      !m.command &&
      (m.segments || []).some(isPlayerSegment)
    if (pending && !sameOpening && (m.role === 'user' || playerLed)) flush()
    if (channel === 'ooc') {
      const content =
        m.content ||
        m.segments
          ?.map((segment) => segment.text)
          .filter(Boolean)
          .join('\n') ||
        ''
      if (content)
        result.push(
          m.role === 'user'
            ? {
                t: 'user',
                key: `${m.id}:ooc-user`,
                v: content,
                turnId: m.turnId,
                messageId: m.id,
                requestId: m.requestId,
                channel,
              }
            : {
                t: 'sys',
                key: `${m.id}:ooc-answer`,
                v: content,
                messageId: m.id,
                segments: m.segments,
                channel,
              }
        )
      continue
    }
    if (m.role === 'user') {
      // 속마음은 사용자 말풍선 없이 결과 카드만 남긴다
      if (m.source === 'command' && m.content === '!속마음') continue
      // 답변이 제한돼 서버가 대신 넣은 고정 문장 — 내가 한 말처럼 보이지 않게 안내 줄로 바꾼다
      if (m.source === 'automatic_recovery') {
        result.push({
          t: 'sys',
          key: `${m.id}:recovery`,
          v: '이 요청에는 답할 수 없어 일상 대화로 이어 갔어요',
        })
        continue
      }
      result.push({
        t: 'user',
        key: `${m.id}:user`,
        v: m.content,
        turnId: m.turnId,
        messageId: m.id,
        requestId: m.requestId,
        channel,
      })
      continue
    }
    if (m.command) {
      const item: ChatItem = {
        t: 'cmd',
        key: `${m.id}:command`,
        data: m.command,
        replyId: m.id,
        snap: snapshot(
          catalog,
          workId,
          m.statusSnapshot || room.status,
          room.affinity
        ),
      }
      if (m.command.kind === 'inner' && m.command.key !== 'inner') {
        // 속마음은 방금 한 말 바로 아래에 붙인다 — 단, 그 뒤에 다른 명령(!폰 등)이 이미 나왔으면 시간 순서대로 맨 끝에 둔다.
        // 끼워 넣으면 나중에 친 속마음이 먼저 친 폰 화면 위로 올라가 순서가 뒤집혀 보였다. 카드 첫 줄이 어떤 말의 속마음인지 인용한다
        let at = -1
        for (let i = result.length - 1; i >= 0; i--) {
          if (result[i].t === 'char') {
            at = i
            break
          }
        }
        const interrupted =
          at >= 0 &&
          result
            .slice(at + 1)
            .some(
              (x) =>
                x.t === 'user' || (x.t === 'cmd' && x.data.kind !== 'inner')
            )
        if (at >= 0 && !interrupted) {
          while (
            result[at + 1]?.t === 'cmd' &&
            (result[at + 1] as { data: CommandData }).data.kind === 'inner'
          )
            at++
          result.splice(at + 1, 0, item)
        } else result.push(item)
      } else result.push(item)
      continue
    }
    if (m.role === 'system') {
      result.push({ t: 'sys', key: `${m.id}:system`, v: m.content })
      continue
    }
    const segments =
      m.segments && m.segments.length
        ? m.segments
        : [{ type: 'speech' as const, text: m.content }]
    segments.forEach((s, index) => {
      const key = `${m.id}:${s.id || index}`
      const replyId = m.turnId ? m.id : undefined
      const detail = {
        segment: s,
        messageId: m.id,
        requestId: m.requestId,
        channel: s.channel,
        ...(m.source === 'opening' ? { source: m.source } : {}),
      }
      if (s.type === 'image') {
        if (s.image && s.image !== lastSceneImage)
          result.push({ t: 'image', key, src: s.image, replyId, ...detail })
        if (s.image) lastSceneImage = s.image
      } else if (s.text) {
        if (s.type === 'narration')
          result.push({ t: 'sit', key, v: s.text, replyId, ...detail })
        else if (isPlayerSegment(s))
          result.push({ t: 'user', key, v: s.text, aiPlayer: true, ...detail })
        else
          result.push({
            t: 'char',
            key,
            v: s.text,
            sp: s.speaker,
            replyId,
            ...detail,
          })
      }
    })
    const portraits = m.characterImages?.filter(
      (portrait): portrait is CharacterImage & { albumId: string } =>
        !!(
          portrait.image &&
          portrait.albumId &&
          (portrait.matched ||
            portrait.emotion?.trim() ||
            portrait.situation?.trim())
        )
    )
    if (portraits) {
      const speakers = new Set(portraits.map((portrait) => portrait.speakerId))
      for (const speakerId of lastPortraitBySpeaker.keys()) {
        if (!speakers.has(speakerId)) lastPortraitBySpeaker.delete(speakerId)
      }
    }
    for (const portrait of portraits || []) {
      const previous = lastPortraitBySpeaker.get(portrait.speakerId)
      if (
        previous?.albumId === portrait.albumId &&
        previous.image === portrait.image
      )
        continue
      const baseKey = `${m.id}:portrait:${portrait.speakerId}:${portrait.albumId}`
      const occurrence = portraitKeyCounts.get(baseKey) || 0
      portraitKeyCounts.set(baseKey, occurrence + 1)
      result.push({
        t: 'image',
        key: occurrence ? `${baseKey}:${occurrence}` : baseKey,
        src: portrait.image,
        portrait,
        speakerName: room.work?.cast?.find(
          (cast) => cast.id === portrait.speakerId
        )?.name,
        replyId: m.turnId ? m.id : undefined,
        messageId: m.id,
        channel,
      })
      lastPortraitBySpeaker.set(portrait.speakerId, {
        albumId: portrait.albumId,
        image: portrait.image,
      })
    }
    if (m.statusSnapshot) {
      const snap = snapshot(catalog, workId, m.statusSnapshot)
      const changed =
        !lastStatus ||
        lastStatus.place !== snap.place ||
        lastStatus.date !== snap.date ||
        stageOf(catalog, lastStatus.aff, workId) !==
          stageOf(catalog, snap.aff, workId)
      // 묶음마다 상태창을 하나씩 남긴다(2026-10-13) — 예전엔 장면이 그대로면 직전 것을 지워 최신 하나만 보였다.
      // 지난 것은 접힌 줄(TurnStatus) — mini(장소 · 날짜 · 관계 단계가 그대로)면 '상태창'으로만, 장면이 바뀐 턴은 장소 · 시간을 적는다
      pending = {
        item: {
          t: 'status',
          key: `${m.id}:status`,
          snap,
          mini: !changed,
          values: m.statusValues ?? pickBoardValues(m.statusSnapshot),
          ...(m.source === 'opening' ? { source: m.source } : {}),
        },
        opening: m.source === 'opening',
      }
      lastStatus = snap
    }
  }
  flush()
  if (channel === 'dialogue' && !lastStatus)
    result.push({
      t: 'status',
      key: `${room.id}:status`,
      snap: snapshot(catalog, workId, room.status, room.affinity),
      mini: false,
      values: room.statusValues ?? pickBoardValues(room.status),
    })
  if (channel === 'dialogue' && room.ending)
    result.push({
      t: 'sys',
      key: `${room.id}:ending`,
      v: `📖 ${room.ending.title} — ${room.ending.line}`,
    })
  return result
}

/** 한 줄 앞에 붙는 답장 머리(말하는 사람)와 답장 끝의 복사 대상 */
/** copy 가 있는 줄이 답장 묶음의 끝 — 그 아래에 답장 도구(복사·다시 쓰기·고치기)를 단다. replyId 는 그 답장의 서버 메시지 id */
type ReplyMeta = {
  head?: {
    speaker: string | null
    speakerId?: string | null
    speakerType?: string
  }
  copy?: string
  replyId?: string
}

const isReplyLine = (x: ChatItem) =>
  x.t === 'sit' || x.t === 'char' || x.t === 'image' || x.t === 'kindImage'

/**
 * 캐릭터 답장 묶음 — 내 말·안내·상태 줄 사이에 이어진 지문·대사·장면 컷을 한 덩어리로 본다.
 * 말하는 사람(아바타·이름)은 대사 위에만 붙인다 — 묶음의 첫 대사, 지문(상황)이 끼어든 뒤 다시 이어지는 대사, 말하는 사람이 바뀐 대사(2026-10-13).
 * 지문 위에는 붙이지 않는다 — 머리가 지문 위에 오면 상황이 캐릭터 말처럼 보였다. 지문은 대사 사이에 따로 서고, 그 뒤 대사엔 프사 · 이름이 다시 나온다.
 * 장면 컷은 PC 에서 옆 무대로 빠져 목록에서 숨으므로 머리·복사 버튼은 글 줄(지문·대사)에만 단다. 마지막 글 줄에 복사할 글 전체를 단다.
 * speaker null = 이 작품의 주인공 캐릭터.
 */
export function replyMeta(
  items: ChatItem[],
  charName: string
): Record<string, ReplyMeta> {
  const meta: Record<string, ReplyMeta> = {}
  const who = (item: Extract<ChatItem, { t: 'char' }>) => ({
    speaker:
      !item.sp ||
      (item.sp === charName &&
        item.segment?.speakerType !== 'npc' &&
        !item.segment?.speakerId)
        ? null
        : item.sp,
    speakerId: item.segment?.speakerId,
    speakerType: item.segment?.speakerType,
    identity: item.segment?.speakerId || item.sp || charName,
  })
  let i = 0
  while (i < items.length) {
    if (!isReplyLine(items[i])) {
      i++
      continue
    }
    let j = i
    while (j < items.length && isReplyLine(items[j])) j++
    const run = items.slice(i, j)
    const texts = run.filter(
      (x): x is Extract<ChatItem, { t: 'sit' | 'char' }> =>
        x.t === 'sit' || x.t === 'char'
    )
    const chars = texts.filter(
      (x): x is Extract<ChatItem, { t: 'char' }> => x.t === 'char'
    )
    if (chars.length) {
      let speaker: ReturnType<typeof who> | null = null
      let prev: ChatItem | null = null
      for (const x of texts) {
        if (x.t !== 'char') {
          prev = x
          continue
        }
        const next = who(x)
        if (
          !speaker ||
          prev?.t !== 'char' ||
          next.identity !== speaker.identity ||
          next.speakerType !== speaker.speakerType
        )
          meta[x.key] = { head: next }
        speaker = next
        prev = x
      }
    }
    if (texts.length) {
      // 장면 컷은 PC 에서 숨으므로 마지막 글 줄에 단다
      const last = texts[texts.length - 1]
      meta[last.key] = {
        ...meta[last.key],
        copy: texts.map((x) => x.v).join('\n\n'),
        replyId: last.replyId,
      }
    }
    i = j
  }
  return meta
}

/** 추천 답장 → 보낼 문장. 지문이 있으면 대사 앞에 붙여 두 줄로 보낸다 (캐릭터가 행동도 보게) */
export function sugText(s: Suggestion): string {
  const say = String(s.text || '')
  const act = sugAct(s)
  return act ? `*${act}*${say ? `\n${say}` : ''}` : say
}

export function sugAct(s: Suggestion): string {
  return String(s.act || '')
    .trim()
    .replace(/^\(([\s\S]*)\)$/, '$1')
}

/** 방의 현재 추천 3개 (v2 curSugs) — 서버가 비워 두면 비운 채로 */
export function currentSuggestions(
  room: Room | undefined,
  fallback: string[]
): Suggestion[] {
  if (!room)
    return fallback.slice(0, 3).map((text) => ({ text, tone: 'approach' }))
  return (room.suggestions || [])
    .map((reply) => (typeof reply === 'string' ? { text: reply } : reply))
    .map((reply) => ({
      ...reply,
      text: String(reply.text || '').trim(),
      act: String(reply.act || '').trim(),
    }))
    .filter((reply) => reply.text)
    .slice(0, 3)
}

/** 오프닝 자동 재생 간격(ms) — 레퍼런스 OPEN_DELAYS */
const OPEN_DELAYS = [
  0, 500, 1700, 3500, 5000, 6300, 7600, 9000, 11500, 12200, 13800, 15300, 16800,
  19300, 20000, 21300, 22600,
]

export function openDelay(i: number) {
  return (
    OPEN_DELAYS[i] ??
    OPEN_DELAYS[OPEN_DELAYS.length - 1] + (i - OPEN_DELAYS.length + 1) * 1300
  )
}
